import { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useT } from "@/i18n";

const DISMISS_KEY = "ct_install_dismissed";

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}
function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
}

// In-app "Install CrickTrack" prompt. Uses the native beforeinstallprompt on
// Android/Chrome, and shows Add-to-Home-Screen guidance on iOS Safari.
export function InstallPrompt() {
  const t = useT();
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY)) return;

    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBIP);

    // iOS never fires beforeinstallprompt — show a manual hint after a moment.
    let tmo;
    if (isIOS()) tmo = setTimeout(() => setVisible(true), 2500);

    window.addEventListener("appinstalled", () => setVisible(false));
    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      if (tmo) clearTimeout(tmo);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      setDeferred(null);
      setVisible(false);
      if (outcome !== "accepted") localStorage.setItem(DISMISS_KEY, "1");
    } else if (isIOS()) {
      setIosHint(true);
    }
  };

  if (!visible) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed inset-x-3 bottom-3 md:left-auto md:right-4 md:w-96 z-[60] fade-up"
    >
      <div className="bg-ink text-white rounded-2xl shadow-2xl border border-white/10 p-4 grain">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="bg-white rounded-xl p-1.5">
              <Logo size={26} showText={false} />
            </div>
            <div>
              <div className="font-head font-bold text-lg leading-tight">{t("Install CrickTrack")}</div>
              <div className="text-xs text-slate-300">{t("Add to your home screen — opens fullscreen, works offline.")}</div>
            </div>
          </div>
          <button onClick={dismiss} data-testid="install-dismiss" className="text-slate-400 hover:text-white p-1 -mr-1 -mt-1">
            <X size={18} />
          </button>
        </div>

        {iosHint ? (
          <div className="mt-3 text-sm text-slate-200 bg-white/5 rounded-lg p-3 flex items-center gap-2" data-testid="install-ios-hint">
            {t("Tap")} <Share size={16} className="inline text-brand" /> {t("in Safari, then")} <b>{t("Add to Home Screen")}</b>.
          </div>
        ) : (
          <div className="mt-3 flex gap-2">
            <button
              onClick={install}
              data-testid="install-confirm"
              className="flex-1 inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-dark text-white font-bold rounded-xl h-10 transition-colors"
            >
              <Download size={16} /> {t("Install app")}
            </button>
            <button onClick={dismiss} className="px-4 h-10 rounded-xl text-slate-300 hover:bg-white/10 text-sm font-semibold">
              {t("Not now")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
